import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ZendeskArticle } from 'interfaces/ZendeskArticle';
import { Container, Theme, Typography, useMediaQuery } from '@mui/material';
import { styled } from '@mui/material/styles';
import Grid from '@mui/material/Unstable_Grid2';

import { useContent } from 'hooks/queries/useContent';
import { useContentAccessError } from 'hooks/useContentAccessError';
import { useToverHead } from 'hooks/useToverHead';
import { NotFound } from 'components/app/NotFound';
import ArticleMetaData from 'components/mytover/content/ArticleMetaData';
import ArticleSectionMenu from 'components/mytover/content/ArticleSectionMenu';
import CenteredCircularProgress from 'components/page/CenteredCircularProgress';
import { DefaultLayout } from 'components/page/layouts/DefaultLayout';
import PageHeaderSection from 'components/page/PageHeaderSection';
import PageSectionContainer from 'components/page/PageSectionContainer';
import ArticleSections from 'components/page/sections/ArticleSections';

interface ArticleContainerProps {
  identifier?: string;
  // Already resolved elsewhere (e.g. ContentPage) — skip the internal fetch.
  article?: ZendeskArticle;
}

const StyledArticle = styled('div')(({ theme }) => ({
  '& table': {
    [theme.breakpoints.only('xs')]: {
      'tableLayout': 'fixed',
      'width': '100%',
      '& td': {
        overflowX: 'auto',
      },
    },
  },
}));

const ArticleContainer: FC<ArticleContainerProps> = ({ identifier, article: preloadedArticle }) => {
  const { t, i18n } = useTranslation();
  const { id: idParam, locale } = useParams();
  const onMobileAndTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const articleId = preloadedArticle ? '' : identifier ?? idParam ?? '';
  const { data, isLoading, error } = useContent(articleId, locale ?? i18n.language);
  const article = preloadedArticle ?? (data?.data as ZendeskArticle | undefined);

  useToverHead({
    title: article?.title ?? '',
    description: article?.seo?.meta_description ?? undefined,
    alternateLinks: article?.alt_links,
    // Not found renders <NotFound />, which is not a page to index.
    noIndex: !isLoading && !article,
  });

  const accessError = useContentAccessError(error);
  if (accessError) {
    return accessError;
  }

  const createMarkup = (content?: any) => ({ __html: content || article!.body! });

  return (
    <DefaultLayout>
      <Container
        maxWidth="xl"
        sx={{
          '& a[name]': {
            display: 'block',
            position: 'relative',
            top: -100,
            visibility: 'hidden',
          },
        }}
      >
        {isLoading ? (
          <CenteredCircularProgress />
        ) : article ? (
          <Grid container sx={{ pb: 8 }}>
            <Grid xs={12} md={3} container component="aside" direction="column">
              <Grid sx={{ ml: onMobileAndTablet ? 0 : 3 }}>
                <ArticleSectionMenu slug={article?.slug} category={article?.category?.id} />
              </Grid>
            </Grid>

            <Grid xs={12} md={8} paddingX={0}>
              <PageHeaderSection titleId="article-title" title={article?.title} crumbs={article?.breadcrumbs} />

              <PageSectionContainer sx={{ py: 0 }}>
                <ArticleMetaData article={article} />
              </PageSectionContainer>

              {/* Translation is per language: without one in the requested language the
                  article has no content, which is worth saying rather than showing an
                  empty page. */}
              {!article.lang ? (
                <PageSectionContainer sx={{ pb: 4 }}>
                  <Typography id="article-unavailable">{t('article.unavailable-in-language')}</Typography>
                </PageSectionContainer>
              ) : article.sections ? (
                <ArticleSections sections={article.sections} />
              ) : (
                article.body && (
                  <PageSectionContainer sx={{ pb: 4 }}>
                    <StyledArticle id="article-body" dangerouslySetInnerHTML={createMarkup()} />
                  </PageSectionContainer>
                )
              )}
            </Grid>
          </Grid>
        ) : (
          <NotFound />
        )}
      </Container>
    </DefaultLayout>
  );
};

export default ArticleContainer;
